import { ExpressCheckoutElement, useElements, useStripe } from '@stripe/react-stripe-js';
import {
  StripeExpressCheckoutElementConfirmEvent,
  StripeExpressCheckoutElementOptions,
  StripeExpressCheckoutElementReadyEvent,
} from '@stripe/stripe-js';
import classNames from 'classnames';
import { useMemo } from 'react';
import { NanoCheckoutFailureReason } from '../../../lib/analytics/events';
import { ShippingAddress } from '../../../lib/checkout/address';
import { buildApplePayRecurringOption } from '../../../lib/checkout/applePay';
import { CheckoutOffer } from '../../../lib/checkout/offer';
import { PriceBreakdown } from '../../../lib/checkout/pricing';
import { shippingAddressFromWallet } from '../../../lib/checkout/walletAddress';
import StripeElementsProvider from '../../../lib/stripe/StripeElementsProvider';
import styles from './CheckoutStep.module.scss';

interface ExpressWalletButtonProps {
  offer: CheckoutOffer;
  /** today's total, which the sheet charges, and the shipping method it must list once it has an address */
  breakdown: PriceBreakdown;
  /**
   * Whether the button is the pay bar's action right now. It stays mounted while hidden, collapsed
   * with `visibility: hidden` rather than removed, so it can report `ready` before it is first shown
   * and keep its state when the bar hands over to Confirm and back.
   */
  shown: boolean;
  onAvailable: (available: boolean) => void;
  /** a wallet button was tapped, which opens the sheet: the wallet's equivalent of pressing Confirm */
  onTapped: () => void;
  /** a purchase is in flight, so a second tap must not start another */
  disabled: boolean;
  /**
   * The sheet was authorized: the purchase takes it from here, with the address the sheet collected.
   * Resolves once the purchase has finished or failed; its errors render on the page.
   */
  onToken: (confirmationTokenId: string, address: ShippingAddress) => Promise<void>;
  /** the sheet's own failure, before a token exists */
  onFailed: (reason: NanoCheckoutFailureReason) => void;
  /**
   * A wallet purchase that failed after the sheet closed. Drawn here, under the button the shopper
   * just used, rather than under the payment card far below it on a phone.
   */
  error: string | null;
}

/**
 * Apple Pay or Google Pay as the pay bar's button: the default way to pay wherever the browser has a
 * wallet. The sheet collects the name, shipping address and phone, so a wallet shopper never touches
 * the address card or the card form.
 *
 * Runs in its own Elements group, in payment mode for today's total, as the shop's hardware-first wallet
 * row does. A sheet in setup mode shows "Amount Pending" rather than the price; in payment mode it shows
 * the total, and the purchase charges the token inline instead of storing it first. A separate group
 * also keeps this element's `submit()` from validating the address and card the shopper is skipping.
 */
export default function ExpressWalletButton(props: ExpressWalletButtonProps) {
  return (
    <StripeElementsProvider amountInCents={props.breakdown.totalInCents}>
      <WalletButton {...props} />
    </StripeElementsProvider>
  );
}

function WalletButton({
  offer,
  breakdown,
  shown,
  onAvailable,
  onTapped,
  disabled,
  onToken,
  onFailed,
  error,
}: ExpressWalletButtonProps) {
  const stripe = useStripe();
  const elements = useElements();

  const options = useMemo<StripeExpressCheckoutElementOptions>(() => {
    // the merchant token request; without it renewals land on a device token
    const applePayRecurring = buildApplePayRecurringOption(offer);
    return {
      shippingAddressRequired: true,
      phoneNumberRequired: true,
      // registration hardcodes US and the plan this flow sells has no non-USD price
      allowedShippingCountries: ['US'],
      paymentMethods: {
        applePay: 'auto',
        googlePay: 'auto',
        // link stays in the payment list below, where the email card drives its lookup
        link: 'never',
        paypal: 'never',
        amazonPay: 'never',
        klarna: 'never',
      },
      buttonType: { applePay: 'buy', googlePay: 'buy' },
      // the tallest stripe allows, closest to the 56px Confirm it stands in for
      buttonHeight: 55,
      ...(applePayRecurring && { applePay: applePayRecurring }),
    };
  }, [offer]);

  const handleReady = ({ availablePaymentMethods }: StripeExpressCheckoutElementReadyEvent) => {
    onAvailable(!!availablePaymentMethods?.applePay || !!availablePaymentMethods?.googlePay);
  };

  const handleConfirm = async (event: StripeExpressCheckoutElementConfirmEvent) => {
    const { shippingAddress, billingDetails } = event;
    const address = shippingAddress ? shippingAddressFromWallet(shippingAddress, billingDetails?.phone) : null;
    if (!stripe || !elements || !address) {
      event.paymentFailed({ reason: 'fail', message: 'Add your full name to the shipping contact and try again.' });
      onFailed('address_error');
      return;
    }

    let confirmationTokenId: string;
    try {
      const submitResult = await elements.submit();
      if (submitResult.error) {
        event.paymentFailed({ reason: 'fail' });
        onFailed('card_declined');
        return;
      }

      const { confirmationToken, error: tokenError } = await stripe.createConfirmationToken({
        elements,
        params: {
          // recurly saves the payment method against a billing address, and the wallet puts only the phone
          // on it, so the shipping contact is the billing contact here as it is on the form
          payment_method_data: {
            billing_details: {
              name: `${address.firstName} ${address.lastName}`,
              phone: address.phone,
              address: {
                line1: address.line1,
                line2: address.line2,
                city: address.city,
                state: address.state,
                postal_code: address.zip,
                country: address.country,
              },
            },
          },
        },
      });
      if (tokenError || !confirmationToken) {
        // the last point paymentFailed may run: stripe closes the sheet once a token exists
        event.paymentFailed({ reason: 'fail' });
        onFailed('card_declined');
        return;
      }
      confirmationTokenId = confirmationToken.id;
    } catch {
      // stripe threw rather than returning an error: close the sheet rather than leave it spinning
      event.paymentFailed({ reason: 'fail' });
      onFailed('service_error');
      return;
    }

    await onToken(confirmationTokenId, address);
  };

  const { totalInCents, shippingInCents, shippingCode } = breakdown;

  return (
    <div
      className={styles.walletButton}
      // takes no space until it is the bar's action and stripe has found a wallet
      style={shown ? undefined : { visibility: 'hidden', height: 0, overflow: 'hidden', margin: 0 }}
    >
      <div className={classNames(disabled && styles.walletBusy)}>
        <ExpressCheckoutElement
          options={options}
          onReady={handleReady}
          onClick={({ resolve }) => {
            onTapped();
            resolve({
              // what the sheet lists above its total: the recurring request has no slot for a one-time
              // item, so without these the tracker only appears in the renewal terms
              lineItems: [
                { name: 'Fi Mini + 6 months of membership', amount: totalInCents - shippingInCents },
                ...(shippingInCents > 0 ? [{ name: 'Shipping', amount: shippingInCents }] : []),
              ],
              shippingRates: [
                {
                  id: shippingCode ?? 'standard',
                  displayName: shippingInCents === 0 ? 'Free shipping' : 'Shipping',
                  amount: shippingInCents,
                },
              ],
            });
          }}
          onConfirm={(event) => void handleConfirm(event)}
        />
      </div>
      {error && <div className={styles.fieldError}>{error}</div>}
    </div>
  );
}
